import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { API, BACKEND } from '../config';

/**
 * ImageUpload — reusable image upload component with preview
 * Props:
 *  - onUpload(url: string | null): called with full URL after upload or null on remove
 *  - currentImage: optional existing image URL to show initially
 *  - className: container class
 *  - shape: 'square' | 'circle' (default 'square')
 *  - placeholder: text shown in empty state
 */
export default function ImageUpload({
  onUpload,
  currentImage = null,
  className = '',
  shape = 'square',
  placeholder = 'Upload photo',
}) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);
  const inputRef = useRef();

  const shapeClass = shape === 'circle' ? 'rounded-full' : 'rounded-xl';

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPG, PNG, WebP)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }
    // Show local preview immediately
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Build full URL from backend base URL
      const fullUrl = res.data.url.startsWith('http')
        ? res.data.url
        : `${BACKEND}${res.data.url}`;
      setPreview(fullUrl);
      onUpload(fullUrl);
      toast.success('Image uploaded!');
    } catch (err) {
      setPreview(currentImage || null);
      toast.error(err.response?.data?.detail || 'Upload failed. Try a smaller image.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setPreview(null);
    onUpload(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={e => handleFile(e.target.files?.[0])}
        data-testid="image-upload-input"
      />

      {preview ? (
        <div className={`relative w-full h-full overflow-hidden ${shapeClass}`}>
          <img
            src={preview}
            alt="Uploaded"
            className={`w-full h-full object-cover ${shapeClass}`}
          />
          {/* Overlay on hover */}
          <div
            className={`absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2 cursor-pointer ${shapeClass}`}
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="w-5 h-5 text-white" />
            <span className="text-white text-xs font-medium">Change</span>
          </div>
          {/* Remove button */}
          <button
            onClick={handleRemove}
            className="absolute top-1 right-1 w-6 h-6 bg-black/70 hover:bg-red-500 text-white rounded-full flex items-center justify-center transition-colors z-10"
            data-testid="image-upload-remove"
          >
            <X className="w-3 h-3" />
          </button>
          {uploading && (
            <div className={`absolute inset-0 bg-black/50 flex items-center justify-center ${shapeClass}`}>
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          className={`w-full h-full border-2 border-dashed border-border hover:border-[#FF4500] hover:bg-[#FF4500]/5 transition-all flex flex-col items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${shapeClass}`}
          data-testid="image-upload-btn"
        >
          {uploading ? (
            <>
              <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
              <span className="text-xs text-muted-foreground">Uploading...</span>
            </>
          ) : (
            <>
              <ImageIcon className="w-5 h-5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground text-center px-1">{placeholder}</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}
